import SearchOffIcon from "@mui/icons-material/SearchOff";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { fetchHelpSearchResults } from "@/api/help";
import { HelpArticleCard } from "@/components/help/HelpArticleCard";
import { useDelayedFlag } from "@/components/help/useDelayedFlag";
import { GfgButton } from "@/components/ui/GfgButton";
import { HELP_SEARCH } from "@/constants/help";
import { apiErrorMessage } from "@/utils/apiError-helper";

const SKELETON_KEYS = ["skeleton-1", "skeleton-2", "skeleton-3"];

export interface HelpSearchResultsViewProps {
  /**
   * The header box's query, trimmed and debounced. Below HELP_SEARCH.MIN_QUERY_LENGTH nothing is
   * fetched and the view renders empty.
   */
  query: string;
  /** Focus this view's heading on show — set when it was reached from inside the panel. */
  focusOnShow: boolean;
  onSelectArticle: (slug: string) => void;
}

/**
 * The panel's search view: the header box's results as the screen view's rows, matches marked,
 * or the design of record's `.hc-empty` block when nothing matches
 * (content/design/client-experience-prototype.html). The count heading sits in a polite live
 * region so a screen reader hears it while focus stays in the search box.
 */
export function HelpSearchResultsView({
  query,
  focusOnShow,
  onSelectArticle,
}: HelpSearchResultsViewProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const {
    data: articles,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["help", "search", query],
    queryFn: () => fetchHelpSearchResults(query),
    enabled: query.length >= HELP_SEARCH.MIN_QUERY_LENGTH,
  });

  const showSkeleton = useDelayedFlag(isLoading);
  const results = isLoading || isError ? undefined : articles;
  const hasOutcome = results !== undefined;

  // The heading only exists once a fetch has settled (Back can land here after the cached results
  // were garbage-collected), so focus follows `hasOutcome`, not just `focusOnShow`.
  useEffect(() => {
    if (focusOnShow && hasOutcome) headingRef.current?.focus();
  }, [focusOnShow, hasOutcome]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `query` is an intentional trigger the body doesn't read — a new result set must start at the top of the pane.
  useEffect(() => {
    rootRef.current?.closest<HTMLElement>("[data-help-pane]")?.scrollTo({ top: 0 });
  }, [query]);

  return (
    <div ref={rootRef} className="mx-auto w-full max-w-[560px] max-[900px]:max-w-none">
      <div aria-live="polite" aria-atomic="true">
        {!!results?.length && (
          <h2
            ref={headingRef}
            tabIndex={-1}
            className="m-0 font-crimson font-normal text-[24px] text-rodeo-black outline-none"
          >
            {results.length} result{results.length === 1 ? "" : "s"} for “{query}”
          </h2>
        )}

        {results?.length === 0 && (
          <div className="flex flex-col items-center px-6 py-[30px] text-center">
            <SearchOffIcon aria-hidden="true" className="text-dark-dust" sx={{ fontSize: 40 }} />
            <h2
              ref={headingRef}
              tabIndex={-1}
              className="m-0 mt-2.5 font-crimson font-normal text-[24px] text-rodeo-black outline-none"
            >
              Nothing matches “{query}”
            </h2>
            <p className="mt-2 mb-0 max-w-[340px] text-[13px] text-graphite leading-normal">
              We have not written this one yet, or it goes by another name in the product. Try a
              word a recipient would use — “didn’t arrive”, “wrong address”, “change the date”.
            </p>
          </div>
        )}
      </div>

      {showSkeleton && (
        <div className="flex flex-col gap-3">
          {SKELETON_KEYS.map((key) => (
            <div key={key} className="h-16 animate-pulse rounded-card bg-wax" aria-hidden="true" />
          ))}
        </div>
      )}

      {isError && (
        <div className="flex flex-col items-center gap-3 rounded-card border border-card-border py-10 text-center">
          <p className="text-[14px] text-graphite">
            {apiErrorMessage(error, "We couldn't search help articles. Please try again.")}
          </p>
          <GfgButton type="button" variant="outline" onClick={() => refetch()}>
            Retry
          </GfgButton>
        </div>
      )}

      {!!results?.length && (
        <div className="mt-5 flex flex-col">
          {results.map((article) => (
            <HelpArticleCard
              key={article.slug}
              article={article}
              onSelect={onSelectArticle}
              highlight={query}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default HelpSearchResultsView;
